import { Chip, Tooltip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';
import apiService from '../../services/api';
import { CycleFormAssignment } from '../../types/api';
import { formWeightsOf } from '../../utils/weights';

export type FormWeights = { title: string; weight: number }[];

/** Weights of the first weighted active generic (summary) form in a cycle, or null. */
export function genericWeightsOf(
  forms: { formType?: string; isActive?: boolean; structure?: any }[]
): FormWeights | null {
  for (const f of forms) {
    if (f.formType !== 'generic' || f.isActive === false) continue;
    const w = formWeightsOf(f.structure?.structure);
    if (w) return w;
  }
  return null;
}

/** Cycle-level weights for screens that don't already hold the form defs (shares the ['formAssignments'] cache). */
export function useGenericWeights(cycleId?: string): FormWeights | null {
  const { data } = useQuery<CycleFormAssignment[]>({
    queryKey: ['formAssignments', cycleId],
    queryFn: () => apiService.getFormAssignmentsByCycle(cycleId!),
    enabled: !!cycleId,
  });
  return genericWeightsOf((data ?? []).map((a) => ({ formType: a.formType, isActive: a.isActive, structure: a.structureSnapshot })));
}

const WeightedChip: React.FC<{ weights: FormWeights }> = ({ weights }) => {
  const { lang } = useDirection();
  return (
    <Tooltip title={weights.map((w) => `${w.title} ${w.weight}%`).join(' · ')}>
      <Chip label={tr('score.weighted', lang)} size="small" variant="outlined" sx={{ height: 16, fontSize: 9, '& .MuiChip-label': { px: '5px' } }} />
    </Tooltip>
  );
};

export default WeightedChip;
